import React from 'react';
import BForm from '../../../Tools/Form/BForm';
import { RequestStatus } from '../../../Tools/ModalWithLoader/ModalWithLoader';
import { ExocetFamilyType, ExocetRadioModeType } from '../Types';
import { ConfigurationAction, DefaultRadioConfig, RadioConfig } from './types';
import { useTranslation } from 'react-i18next';
import { array, object, string } from 'yup';
import ClientsAndContracts from '../../../DndComponents/ContractsDnd/ClientsAndContracts';
import { FormikValues } from 'formik';
import { MuiLikeBorderedBox } from '../../../Tools/Form/FormFieldWrapper';
import CustomSwitchField from '../../../CustomSwitchField/CustomSwitchField';
import axios from 'axios';
import { Client, OnChangeArgs } from '../../../DndComponents/ContractsDnd/types';
import { isEqual, isObject } from 'lodash';
import { getAddedFamilies, getSyncedDefaultFamilies, translate } from './utils';
import { useGlobalContext } from '@applications-terrains/birdz-react-library';

const ConfigurationForm = ({
  setRequestStatus,
  action,
  setAction,
  config,
  moduleFamilies,
  radioModes,
  setErrorMessage,
  triggerRefresh,
  clients,
  associatedDefaultConfig,
  notif
}: {
  setRequestStatus: (value: RequestStatus) => void;
  action: ConfigurationAction;
  setAction: (value: ConfigurationAction, config?: RadioConfig) => void;
  config: RadioConfig | null;
  moduleFamilies?: ExocetFamilyType[];
  radioModes?: ExocetRadioModeType[];
  setErrorMessage: (value: string | null) => void;
  triggerRefresh: () => void;
  clients: Client[];
  associatedDefaultConfig: DefaultRadioConfig | null;
  // BirdzNotifOptions n'est pas exporté par le barrel de la BRL, d'où ce type local
  notif: (options: { content: string; type: 'success' | 'error' | 'info' }) => void;
}) => {
  const { t } = useTranslation();
  const radioModeOption = radioModes?.find((mode) => mode.Id === config?.radio_mode);
  const endpoint = '/api/boi/params/configuration-profiles/';
  const { bgColor } = useGlobalContext();
  const isDark = bgColor === 'dark-mode';

  /**
   * Répercute sur la configuration par défaut associée les familles retirées du
   * profil. Le profil vient d'être enregistré : une erreur ici ne remet pas en
   * cause cette sauvegarde, on la signale donc à part plutôt que de faire
   * échouer tout le formulaire.
   */
  const syncAssociatedDefaultConfig = async (profileFamilies: number[]) => {
    if (action !== 'edit' || !associatedDefaultConfig?.id) return;

    const syncedFamilies = getSyncedDefaultFamilies(
      associatedDefaultConfig.families,
      profileFamilies
    );
    if (isEqual(syncedFamilies, associatedDefaultConfig.families)) return;

    try {
      await axios.put(`/api/boi/params/default-configurations/${associatedDefaultConfig.id}/`, {
        profile: associatedDefaultConfig.profile,
        families: syncedFamilies,
        customers: associatedDefaultConfig.customers || [],
        contracts: associatedDefaultConfig.contracts || []
      });
      notif({ content: t('equipmentConfiguration.defaultConfigFamiliesSynced'), type: 'info' });
    } catch {
      notif({ content: t('equipmentConfiguration.defaultConfigFamiliesSyncError'), type: 'error' });
    }
  };

  const defaultConfig = {
    ...config,
    name: action !== 'duplicate' ? config?.name : config?.name + ' - copy' || '',
    dataset_code: config?.dataset_code || '',
    authorisedContracts: config?.authorised_contracts || [],
    excludedContracts: config?.excluded_contracts || [],
    authorisedCustomers: config?.authorised_customers || [],
    excludedCustomers: config?.excluded_customers || [],
    autoconnect: config?.autoconnect ?? false,
    dst: config?.dst ?? false,
    hourly_index: config?.hourly_index ?? false,
    radio_mode: radioModeOption
      ? { label: radioModeOption.Label, value: radioModeOption.Id }
      : null,
    families:
      config?.families
        .map((familyId) => {
          const family = moduleFamilies?.find((fam) => fam.Id === familyId);
          return family ? { label: translate(family.Name), value: family.Id } : null;
        })
        .filter(Boolean) || []
  };

  const formValues = [
    {
      name: 'name',
      label: t('equipmentConfiguration.name'),
      validation: string().required(t('equipmentConfiguration.nameRequired'))
    },
    {
      name: 'dataset_code',
      label: t('equipmentConfiguration.datasetCodeLabel'),
      validation: string().required(t('equipmentConfiguration.datasetRequired'))
    },
    {
      name: 'families',
      label: t('equipmentConfiguration.families'),
      options: {
        values:
          moduleFamilies?.map((family) => ({
            label: translate(family.Name),
            value: family.Id
          })) || []
      },
      validation: array().min(1, t('interventionTypesForm.errors.familieMin')),
      multiple: true
    },
    {
      name: 'radio_mode',
      label: t('equipmentConfiguration.radioMode'),
      options: {
        values:
          radioModes?.map((mode) => ({
            label: mode.Label,
            value: mode.Id
          })) || []
      },
      validation: object({
        label: string(),
        value: string().required(t('equipmentConfiguration.requiredRadioModes'))
      })
        .nonNullable()
        .required(t('equipmentConfiguration.requiredRadioModes'))
    },
    {
      name: 'dst',
      label: '',
      type: 'blank'
    },
    {
      name: 'autoconnect',
      label: '',
      type: 'blank'
    },
    {
      name: 'hourly_index',
      label: '',
      type: 'blank'
    },
    {
      name: 'switchfields',
      label: '',
      render: (_: any, __: any, values: FormikValues) => {
        return (
          <MuiLikeBorderedBox sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: -6 }}>
            <CustomSwitchField name="autoconnect" label={'Auto-connect'} inForm />
            <CustomSwitchField name="dst" label={'DST'} inForm />
            {values.families.find(
              (family: { label: string; value: number }) => family.value === 1
            ) && (
              <CustomSwitchField
                name="hourly_index"
                label={t('equipmentConfiguration.hourly_index')}
                inForm
              />
            )}
          </MuiLikeBorderedBox>
        );
      }
    },
    {
      name: 'contracts',
      label: t('equipmentConfiguration.contracts'),
      render: (_: string[], onChange: (fieldName: string, data?: number[]) => void) => {
        const handleChange = ({
          authorisedContracts,
          excludedContracts,
          authorisedCustomers,
          excludedCustomers
        }: OnChangeArgs) => {
          onChange('authorisedContracts', authorisedContracts);
          onChange('excludedContracts', excludedContracts);
          onChange('authorisedCustomers', authorisedCustomers);
          onChange('excludedCustomers', excludedCustomers);
        };
        return (
          <ClientsAndContracts
            target="profile"
            onChange={handleChange}
            data={clients}
            initialAuthorisedContracts={defaultConfig?.authorisedContracts}
            initialExcludedContracts={defaultConfig?.excludedContracts}
            initialAuthorisedCustomers={defaultConfig?.authorisedCustomers}
            initialExcludedCustomers={defaultConfig?.excludedCustomers}
          />
        );
      }
    }
  ];

  return (
    <BForm
      inModal
      columns={1}
      values={defaultConfig || {}}
      fields={formValues}
      buttonsContainerSx={{
        position: 'absolute',
        bottom: 0,
        right: 2,
        backgroundColor: isDark ? '#1E2235' : '#FFF',
        borderTop: '1px solid',
        borderColor: 'divider',
        padding: 2
      }}
      sx={{ pb: 10, pt: 1 }}
      onSubmit={async (values) => {
        setRequestStatus('pending');
        const method =
          action === 'duplicate' || ((!config || !config.id) && action === 'add') ? 'post' : 'put';
        const url = `${endpoint}${method === 'post' ? '' : +(config as RadioConfig).id + '/'}`;
        const payload = {
          ...config,
          name: values.name,
          dataset_code: values.dataset_code,
          families: values.families.map((fam: { label: string; value: number }) => fam.value),
          radio_mode: values.radio_mode.value,
          autoconnect: values.autoconnect,
          dst: values.dst,
          hourly_index: values.hourly_index,
          authorised_contracts: values.authorisedContracts,
          excluded_contracts: values.excludedContracts,
          authorised_customers: values.authorisedCustomers,
          excluded_customers: values.excludedCustomers
        };

        return axios[method](url, payload)
          .then(async (res) => {
            await syncAssociatedDefaultConfig(payload.families);
            setRequestStatus('success');
            triggerRefresh();
            if (action === 'edit') {
              // L'ajout d'une famille n'est pas propagé automatiquement (contrairement
              // au retrait) : on le rappelle à l'utilisateur avant de refermer.
              const addedFamilies = getAddedFamilies(config?.families || [], payload.families);
              if (addedFamilies.length === 0) {
                setTimeout(() => {
                  setAction(null);
                }, 1500);
              } else if (associatedDefaultConfig) {
                setAction('askingToEditDefault');
              } else {
                // Aucune configuration par défaut à modifier : on propose d'en créer une.
                // res.data remet selectedConfig à jour, donc avec la famille ajoutée.
                setAction('askingForDefaultAfterEdit', res.data);
              }
            } else {
              setAction('askingForDefault', res.data);
            }
          })
          .catch((err) => {
            const errors = isObject(err.response?.data)
              ? Object.entries(err.response?.data)
              : JSON.stringify(err.response?.data) || 'An error occurred';
            setRequestStatus('error');
            if (Array.isArray(errors)) {
              const formattedErrors = errors.map(([field, messages]) => {
                const fieldName = formValues.find((f) => f.name === field)?.label || field;
                const message = Array.isArray(messages) ? messages.join(' ') : messages;
                return `${fieldName}: ${message}`;
              });
              setErrorMessage(formattedErrors.join(' '));
            } else {
              setErrorMessage(errors);
            }
          });
      }}
      onCancel={() => setAction(null)}
    />
  );
};

export default ConfigurationForm;
